import { useState, useEffect, useCallback } from 'react';

import InsertInvitationRoundedIcon from '@mui/icons-material/InsertInvitationRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import * as Styled from './AssistanceStickySection.style';

import { useProductLandingContext } from '../../contexts/useProductLanding';
import { useProductLandingClickstreamProps } from '../../hooks/useProductLandingClickstreamProps';

const SCROLL_THRESHOLD = 0.1;

export const AssistanceStickySection = () => {
  const { productConfig } = useProductLandingContext();
  const personalizationClickstreamProps = useProductLandingClickstreamProps();
  const { assistanceSticky } = productConfig;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;

    if (docHeight > 0) {
      setIsVisible(scrollTop / docHeight >= SCROLL_THRESHOLD);
    }
  }, []);

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, isMobile]);

  if (!assistanceSticky || (isMobile && !isVisible)) {
    return null;
  }

  return (
    <Styled.StickyContainer>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        gap={2}
      >
        <Text size="md" weight="medium" color="primary">
          <FormattedMessage id={assistanceSticky.title} />
        </Text>
        <Styled.ActionsStack direction="row" gap={1.5}>
          <Button
            variant="outlined"
            size="md"
            fullWidth
            href={assistanceSticky.primaryAction.href}
            target="_blank"
            startIcon={<PhoneRoundedIcon />}
            clickstreamProps={{
              ...personalizationClickstreamProps,
              elementName: 'assistance_sticky_primary_action',
              destination: assistanceSticky.primaryAction.href,
            }}
          >
            <FormattedMessage id={assistanceSticky.primaryAction.title} />
          </Button>
          <Button
            variant="contained"
            size="md"
            fullWidth
            href={assistanceSticky.secondaryAction.href}
            target="_blank"
            startIcon={<InsertInvitationRoundedIcon />}
            clickstreamProps={{
              ...personalizationClickstreamProps,
              elementName: 'assistance_sticky_secondary_action',
              destination: assistanceSticky.secondaryAction.href,
            }}
          >
            <FormattedMessage id={assistanceSticky.secondaryAction.title} />
          </Button>
        </Styled.ActionsStack>
      </Stack>
    </Styled.StickyContainer>
  );
};
